// src/pages/calendar/CalendarPage.tsx
// The calendar hub: day/week/month/agenda grid, calendar visibility panel,
// click-to-create, event detail rail. Home for the daily calendar tasks.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useCalendar, useI18n } from '@react/hooks';
import { Button, Alert } from '@react/components';
import { useScope } from '@/hooks/useScope';
import { ListMyEvents } from '@core/api/endpoints/calendar/list_my_events';
import { ListEvents } from '@core/api/endpoints/calendar/list_events';
import type { CalendarEvent } from '@core/types/calendar';
import type { PaginatedRequest } from '@core/types/pagination';
import { CalendarGrid } from '@/features/calendar/CalendarGrid';
import { EventDialog } from '@/features/calendar/EventDialog';
import { EventDetailRail } from '@/features/calendar/EventDetailRail';
import { ImportExportDialog } from '@/features/calendar/ImportExportDialog';
import {
  errMessage,
  fmtDate,
  fmtMonthTitle,
  shiftAnchor,
  startOfWeek,
  type CalendarViewMode,
  viewRange,
} from '@/features/calendar/time';

const VIEWS: CalendarViewMode[] = ['day', 'week', 'month', 'agenda'];

export const CalendarPage = observer(() => {
  const { t, locale } = useI18n();
  const scope = useScope();
  const calendarStore = useCalendar();

  const [view, setView] = useState<CalendarViewMode>('week');
  const [anchor, setAnchor] = useState(new Date());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hiddenCalendars, setHiddenCalendars] = useState<Set<string>>(new Set());
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogEvent, setDialogEvent] = useState<CalendarEvent | null>(null);
  const [dialogStart, setDialogStart] = useState<Date | undefined>(undefined);
  const [menuOpen, setMenuOpen] = useState(false);
  const [ioDialog, setIoDialog] = useState<'import' | 'export' | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Calendars for the left panel.
  useEffect(() => {
    calendarStore.ListCalendars({
      pagination: { cursor: '', limit: 100, direction: 'forward' },
    } as PaginatedRequest).catch(() => {});
  }, []);

  const { start, end } = useMemo(() => viewRange(view, anchor), [view, anchor]);

  const loadEvents = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await ListMyEvents({
        pagination: { cursor: '', limit: 500, direction: 'forward' },
        time_min: start.toISOString(),
        time_max: end.toISOString(),
      } as unknown as PaginatedRequest);
      setEvents(res.events?.events ?? []);
    } catch {
      // Fall back to per-calendar listing when the aggregate endpoint is unavailable.
      try {
        const all: CalendarEvent[] = [];
        for (const c of calendarStore.items) {
          if (!c.entity?.id) continue;
          const res = await ListEvents(c.entity.id, {
            pagination: { cursor: '', limit: 200, direction: 'forward' },
            time_min: start.toISOString(),
            time_max: end.toISOString(),
          } as unknown as PaginatedRequest);
          all.push(...(res.events?.events ?? []));
        }
        setEvents(all);
      } catch (e2) {
        setError(errMessage(e2, t('calendar.loadError')));
      }
    } finally {
      setIsLoading(false);
    }
  }, [scope.productId, start.getTime(), end.getTime(), calendarStore.items.length]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Close overflow menu on outside click.
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  const calendarColors = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of calendarStore.items) {
      if (c.entity?.id && c.color) m.set(c.entity.id, c.color);
    }
    return m;
  }, [calendarStore.items]);

  const visibleEvents = useMemo(
    () => events.filter((ev) => !ev.calendar?.id || !hiddenCalendars.has(ev.calendar.id)),
    [events, hiddenCalendars],
  );

  const title = useMemo(() => {
    if (view === 'month') return fmtMonthTitle(anchor, locale);
    if (view === 'day') return fmtDate(anchor, locale);
    if (view === 'agenda') return `${fmtDate(anchor, locale)} +30`;
    const ws = startOfWeek(anchor);
    return `${fmtDate(ws, locale)} – ${fmtDate(new Date(ws.getTime() + 6 * 86400000), locale)}`;
  }, [view, anchor, locale]);

  const openCreate = (startAt?: Date) => {
    setDialogEvent(null);
    setDialogStart(startAt);
    setDialogOpen(true);
  };

  const openEdit = (ev: CalendarEvent) => {
    setDialogEvent(ev);
    setDialogStart(undefined);
    setDialogOpen(true);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
        <h1 className="mr-2 text-lg font-semibold text-foreground">{t('calendar.title')}</h1>
        <Button size="sm" onClick={() => openCreate()}>
          {t('calendar.newEvent')}
        </Button>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" aria-label={t('common.previous')} onClick={() => setAnchor((a) => shiftAnchor(view, a, -1))}>
            ‹
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setAnchor(new Date())}>
            {t('calendar.today')}
          </Button>
          <Button size="sm" variant="ghost" aria-label={t('common.next')} onClick={() => setAnchor((a) => shiftAnchor(view, a, 1))}>
            ›
          </Button>
        </div>
        <span className="text-sm font-medium text-foreground">{title}</span>
        <div className="ml-auto flex items-center gap-1">
          <div role="tablist" aria-label={t('calendar.viewSwitcher')} className="flex rounded-md border border-border p-0.5">
            {VIEWS.map((v) => (
              <button
                key={v}
                role="tab"
                aria-selected={view === v}
                onClick={() => setView(v)}
                className={`rounded px-2.5 py-1 text-sm ${
                  view === v ? 'bg-muted font-medium text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t(`calendar.view.${v}`)}
              </button>
            ))}
          </div>
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              aria-label={t('common.actions')}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
              className="rounded-md border border-border px-2 py-1.5 text-sm text-foreground hover:bg-muted"
            >
              ⋯
            </button>
            {menuOpen && (
              <div className="absolute right-0 z-40 mt-1 w-48 rounded-md border border-border bg-popover py-1 shadow-md">
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); setIoDialog('import'); }}
                  className="block w-full px-3 py-1.5 text-left text-sm text-foreground hover:bg-muted"
                >
                  {t('calendar.importExport.importTitle')}
                </button>
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); setIoDialog('export'); }}
                  className="block w-full px-3 py-1.5 text-left text-sm text-foreground hover:bg-muted"
                >
                  {t('calendar.importExport.exportTitle')}
                </button>
                <Link
                  to="/calendar/booking"
                  onClick={() => setMenuOpen(false)}
                  className="block px-3 py-1.5 text-sm text-foreground hover:bg-muted"
                >
                  {t('calendar.bookingLinks.title')}
                </Link>
                <Link
                  to="/calendar/settings"
                  onClick={() => setMenuOpen(false)}
                  className="block px-3 py-1.5 text-sm text-foreground hover:bg-muted"
                >
                  {t('common.settings')}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* Calendars panel */}
        <aside className="hidden w-52 shrink-0 space-y-1 overflow-y-auto border-r border-border p-3 md:block">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('calendar.myCalendars')}
            </span>
            <Link to="/calendar/settings?tab=calendars" className="text-xs text-primary hover:underline">
              {t('common.manage')}
            </Link>
          </div>
          {calendarStore.items.length === 0 && !calendarStore.isLoading && (
            <p className="text-sm text-muted-foreground">{t('calendar.noCalendars')}</p>
          )}
          {calendarStore.items.map((c) => {
            const id = c.entity?.id ?? '';
            const visible = !hiddenCalendars.has(id);
            return (
              <label key={id} className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm text-foreground hover:bg-muted">
                <input
                  type="checkbox"
                  checked={visible}
                  onChange={() =>
                    setHiddenCalendars((prev) => {
                      const next = new Set(prev);
                      if (next.has(id)) next.delete(id);
                      else next.add(id);
                      return next;
                    })
                  }
                />
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: c.color || 'hsl(var(--primary))' }}
                  aria-hidden="true"
                />
                <span className="truncate">{c.name}</span>
              </label>
            );
          })}
        </aside>

        {/* Grid */}
        <main className="min-w-0 flex-1 overflow-hidden" aria-busy={isLoading}>
          {error && (
            <div className="p-4">
              <Alert variant="error">{error}</Alert>
              <Button size="sm" variant="secondary" className="mt-2" onClick={loadEvents}>
                {t('calendar.retry')}
              </Button>
            </div>
          )}
          {!error && isLoading && events.length === 0 && (
            <div className="space-y-2 p-4">
              <div className="h-8 w-1/3 animate-pulse rounded bg-muted" />
              <div className="h-64 animate-pulse rounded bg-muted" />
            </div>
          )}
          {!error && (!isLoading || events.length > 0) && (
            <CalendarGrid
              view={view}
              anchor={anchor}
              events={visibleEvents}
              calendarColors={calendarColors}
              selectedEventId={selectedEventId}
              onSlotClick={(s) => openCreate(s)}
              onEventClick={(ev) => setSelectedEventId(ev.entity?.id ?? null)}
            />
          )}
        </main>

        {/* Detail rail */}
        {selectedEventId && (
          <EventDetailRail
            eventId={selectedEventId}
            calendars={calendarStore.items}
            onClose={() => setSelectedEventId(null)}
            onEdit={openEdit}
            onChanged={loadEvents}
          />
        )}
      </div>

      <EventDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        event={dialogEvent}
        calendars={calendarStore.items}
        initialStart={dialogStart}
        onSaved={loadEvents}
      />
      <ImportExportDialog
        open={ioDialog !== null}
        mode={ioDialog ?? 'import'}
        calendars={calendarStore.items}
        onClose={() => setIoDialog(null)}
        onImported={loadEvents}
      />
    </div>
  );
});
